/**
 * 🔔 Notifications Push Natives pour iOS/Android
 * Utilise @capacitor/push-notifications
 * 
 * - iOS: Utilise APNs (Apple Push Notification Service)
 * - Android: Utilise Firebase Cloud Messaging (FCM)
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { apiClient, API_URL } from './http';

const IS_NATIVE = Capacitor.isNativePlatform();
const IS_ANDROID = Capacitor.getPlatform() === 'android';
const IS_IOS = Capacitor.getPlatform() === 'ios';

// Store pour les callbacks de navigation (sera défini par App.js)
let navigationCallback = null;
let toastCallback = null;
let isAppReady = false;

// 🔧 Store pour le userId actuel (garanti d'être disponible)
let currentUserId = null;
let pendingToken = null;

/**
 * Marquer l'app comme prête (appelé depuis App.js après le premier rendu complet)
 */
export function setAppReady(ready = true) {
  isAppReady = ready;
}

/**
 * Définir le callback de navigation (appelé depuis App.js)
 */
export function setNavigationCallback(callback) {
  navigationCallback = callback;
}

/**
 * Définir le callback de toast (appelé depuis App.js)
 */
export function setToastCallback(callback) {
  toastCallback = callback;
}

/**
 * 🔧 Réessayer d'enregistrer un token en attente
 * Appelé après le login réussi
 */
export async function retryPendingTokenRegistration(userId) {
  if (!pendingToken || !userId) {
    console.log('📱 No pending token to register or no userId');
    return false;
  }
  
  console.log(`📱 Retrying pending token registration for user ${userId.substring(0, 8)}...`);
  const platform = IS_IOS ? 'ios' : 'android';
  
  try {
    const success = await registerTokenWithBackend(pendingToken, userId, platform);
    if (success) {
      console.log('📱 Pending token registration SUCCEEDED');
      pendingToken = null;
    }
    return success;
  } catch (error) {
    console.error('📱 Pending token registration failed:', error);
    return false;
  }
}

/**
 * 🔧 Helper pour enregistrer le token sur le backend
 * Avec retry automatique
 */
async function registerTokenWithBackend(token, userId, platform, retryCount = 0) {
  const maxRetries = 3;
  
  if (!userId) {
    console.warn('📱 registerTokenWithBackend: No userId, saving token for later');
    pendingToken = token;
    return false;
  }
  
  try {
    console.log(`📱 Registering push token for user ${userId.substring(0, 8)}... (attempt ${retryCount + 1})`);
    
    // Détecter l'environnement APNs pour iOS
    // TestFlight et Dev builds utilisent Sandbox, App Store utilise Production
    // On envoie "unknown" et le backend essaiera les deux
    let apnsEnvironment = 'unknown';
    if (platform === 'ios') {
      // On ne peut pas détecter facilement si c'est TestFlight ou App Store
      // Le backend va essayer les deux environnements
      apnsEnvironment = 'unknown';
    }
    
    // Utiliser fetch directement pour éviter tout problème avec apiClient
    const response = await fetch(`${API_URL}/api/push/register-device`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        device_token: token,
        platform: platform,
        apns_environment: apnsEnvironment,
      }),
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log(`✅ Push token registered successfully:`, data);
      pendingToken = null; // Clear pending token
      return true;
    } else {
      const errorText = await response.text();
      console.error(`❌ Failed to register token (${response.status}):`, errorText);
      
      // Retry si c'est une erreur serveur
      if (response.status >= 500 && retryCount < maxRetries) {
        console.log(`📱 Retrying in 2 seconds...`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return registerTokenWithBackend(token, userId, platform, retryCount + 1);
      }
      return false;
    }
  } catch (error) {
    console.error('📱 Error registering token:', error);
    
    // Retry sur erreur réseau
    if (retryCount < maxRetries) {
      console.log(`📱 Retrying in 2 seconds...`);
      await new Promise(resolve => setTimeout(resolve, 2000));
      return registerTokenWithBackend(token, userId, platform, retryCount + 1);
    }
    return false;
  }
}

/**
 * 📱 Réinitialiser le badge de l'icône iOS à 0
 * Doit être appelé quand l'app s'ouvre
 */
export async function clearIOSBadge() {
  if (!IS_NATIVE) {
    return;
  }
  
  try {
    // 1. Supprimer toutes les notifications délivrées du centre de notifications
    if (PushNotifications.removeAllDeliveredNotifications) {
      await PushNotifications.removeAllDeliveredNotifications();
      console.log('📱 All delivered notifications removed');
    }
    
    // 2. Utiliser le Badge plugin pour réinitialiser le badge de l'icône
    const { Badge } = await import('@capawesome/capacitor-badge');
    
    // Vérifier les permissions (iOS)
    const permResult = await Badge.checkPermissions();
    if (permResult.display !== 'granted') {
      const reqResult = await Badge.requestPermissions();
      console.log('📱 Badge permissions:', reqResult.display);
    }
    
    // Clear le badge
    await Badge.clear();
    console.log('📱 iOS badge cleared successfully!');
    
  } catch (error) {
    console.warn('📱 Error clearing iOS badge:', error);
  }
}

/**
 * Initialiser les notifications push natives
 * Fonctionne sur iOS (APNs) et Android (FCM)
 */
export async function initNativePushNotifications(passedUserId) {
  if (!IS_NATIVE) {
    console.log('📱 Not a native platform, skipping push init');
    return null;
  }

  console.log(`📱 Initializing push notifications for ${IS_IOS ? 'iOS' : 'Android'}...`);

  // OPTIMISÉ: Attendre que l'app soit prête (max 1 seconde au lieu de 10)
  if (!isAppReady) {
    let attempts = 0;
    while (!isAppReady && attempts < 5) {
      await new Promise(resolve => setTimeout(resolve, 200));
      attempts++;
    }
  }

  try {
    // Vérifier les permissions actuelles
    let permStatus;
    try {
      permStatus = await PushNotifications.checkPermissions();
      console.log('📱 Current permission status:', permStatus.receive);
    } catch (permError) {
      console.warn('📱 Error checking permissions:', permError);
      return null;
    }

    // Demander la permission si nécessaire
    if (permStatus.receive === 'prompt') {
      try {
        console.log('📱 Requesting push permission...');
        permStatus = await PushNotifications.requestPermissions();
        console.log('📱 Permission result:', permStatus.receive);
      } catch (reqError) {
        console.warn('📱 Error requesting permissions:', reqError);
        return null;
      }
    }

    if (permStatus.receive !== 'granted') {
      console.log('📱 Push notifications not granted');
      return null;
    }

    // 🔧 Stocker le userId dans la variable du module
    currentUserId = passedUserId;
    console.log(`📱 Stored userId for push registration: ${currentUserId?.substring(0, 8)}...`);

    // Supprimer les anciens listeners pour éviter les doublons
    await PushNotifications.removeAllListeners();

    // Écouter l'événement de registration (récupère le token)
    PushNotifications.addListener('registration', async (token) => {
      console.log('📱 Got push token:', token.value?.substring(0, 20) + '...');
      console.log('📱 Full token length:', token.value?.length);
      console.log('📱 Current userId at registration:', currentUserId?.substring(0, 8) || 'NOT SET');
      
      const platform = IS_IOS ? 'ios' : 'android';
      
      // Utiliser le userId stocké dans le module (le plus fiable)
      const userId = currentUserId || passedUserId;
      
      if (!userId) {
        console.warn('📱 No userId available at registration, saving token for later');
        pendingToken = token.value;
        return;
      }
      
      console.log(`📱 Attempting to register token with backend for user ${userId.substring(0, 8)}...`);
      
      try {
        const success = await registerTokenWithBackend(token.value, userId, platform);
        console.log(`📱 Token registration ${success ? 'SUCCEEDED' : 'FAILED'}`);
        
        if (!success) {
          // Save for later retry
          pendingToken = token.value;
          console.log('📱 Token saved for retry later');
        }
      } catch (regError) {
        console.error('📱 Exception during token registration:', regError);
        pendingToken = token.value;
      }
    });

    // Écouter les erreurs de registration
    PushNotifications.addListener('registrationError', (error) => {
      console.error('📱 Push registration error:', error);
    });

    // Écouter les notifications reçues quand l'app est au premier plan
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('📩 Push notification received (foreground):', notification.title);
      
      // Émettre un événement pour que l'UI puisse réagir
      window.dispatchEvent(new CustomEvent('pushNotification', {
        detail: {
          title: notification.title,
          body: notification.body,
          data: notification.data
        }
      }));
      
      // Optionnel: Afficher un toast si le callback est défini
      if (toastCallback) {
        toastCallback({
          title: notification.title,
          body: notification.body
        });
      }
    });

    // Écouter les actions sur les notifications (tap quand app en arrière-plan)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('👆 Push notification tapped:', action.notification?.data);
      const data = action.notification?.data || {};
      const type = data.type;
      
      // Petit délai pour s'assurer que l'app est prête
      setTimeout(() => {
        let targetRoute = '/dashboard';
        
        switch (type) {
          case 'chat_message':
            // Ouvrir la conversation avec l'expéditeur
            if (data.from_user_id) {
              targetRoute = `/chat/${data.from_user_id}`;
            } else {
              targetRoute = '/friends';
            }
            break;
          case 'new_mission':
            targetRoute = '/washer';
            break;
          case 'mission_accepted':
          case 'washer_arrived':
          case 'wash_started':
          case 'wash_completed':
            targetRoute = '/ondemand';
            break;
          case 'payment_received':
            targetRoute = '/washer';
            break;
          case 'application_status':
            targetRoute = '/become-washer';
            break;
          case 'admin_store_review':
            targetRoute = '/admin';
            break;
          default:
            targetRoute = '/dashboard';
        }
        
        // Utiliser le callback de navigation si disponible, sinon fallback
        if (navigationCallback) {
          navigationCallback(targetRoute);
        } else {
          window.location.href = targetRoute;
        }
      }, 100);
    });

    // Enregistrer pour recevoir les notifications
    try {
      console.log('📱 Registering for push notifications...');
      await PushNotifications.register();
      console.log('✅ Push notification registration initiated');
    } catch (regError) {
      console.error('📱 Error registering for push:', regError);
      return null;
    }

    return true;

  } catch (error) {
    console.error('📱 Error initializing push notifications:', error);
    return null;
  }
}

/**
 * Désinscrire l'appareil des notifications push
 */
export async function unregisterNativePush(userId) {
  if (!IS_NATIVE) return;

  try {
    // Informer le backend
    await apiClient.post(`${API_URL}/api/notifications/unregister-token`, {
      token: '', // Le backend identifiera le token via la session
    });
    
    // Supprimer tous les listeners
    await PushNotifications.removeAllListeners();
    
    console.log('✅ Push notifications unregistered');
  } catch (error) {
    console.error('📱 Error unregistering push:', error);
  }
}

/**
 * Vérifier si les notifications push sont activées
 */
export async function checkNativePushStatus() {
  if (!IS_NATIVE) {
    return { supported: false, enabled: false };
  }

  try {
    const permStatus = await PushNotifications.checkPermissions();
    return {
      supported: true,
      enabled: permStatus.receive === 'granted',
      status: permStatus.receive,
      platform: IS_IOS ? 'ios' : 'android'
    };
  } catch (error) {
    console.error('📱 Error checking push status:', error);
    return { supported: false, enabled: false, error: error.message };
  }
}

/**
 * 🔧 Réenregistrer un token en attente (appelé si login tardif)
 */
export async function registerPendingPushToken(userId) {
  if (!IS_NATIVE || !pendingToken || !userId) {
    return false;
  }
  
  console.log('📱 Registering pending push token for user', userId.substring(0, 8) + '...');
  currentUserId = userId;
  
  const platform = IS_IOS ? 'ios' : 'android';
  return await registerTokenWithBackend(pendingToken, userId, platform);
}

export default {
  initNativePushNotifications,
  unregisterNativePush,
  checkNativePushStatus,
  registerPendingPushToken,
  setAppReady,
  setNavigationCallback,
  setToastCallback,
};
